import {gsap} from 'gsap';

const REVEAL_RATIO = 0.75;

interface TColumn {
    viewport: HTMLElement;
    reel: HTMLElement;
    place: number;
    baseWidth: number;
    maxStep: number;
    revealStart: number;
    stepProxy: {value: number};
}

class Odometr {
    private counterWrapper!: HTMLElement;
    private numbersWrapper!: HTMLElement;
    private columns: TColumn[] = [];
    private valueProxy: {value: number} = {value: 0};
    private digitHeight: number = 0;
    private maxValue: number = 100;

    public init(counter: HTMLElement, maxValue: number = 100): void {
        this.counterWrapper = counter;
        this.maxValue = maxValue;
        this.valueProxy.value = 0;
        this.numbersWrapper = this.resolveNumbersWrapper(counter);

        this.buildColumns();
        this.measure();
        this.render(this.valueProxy.value);
    }

    public createTween(value: number, vars: gsap.TweenVars = {}): gsap.core.Timeline {
        const nextValue = this.clamp(value, 0, this.maxValue);
        const timeline = gsap.timeline({
            onStart: () => {
                this.measure();
            },
            onUpdate: () => {
                this.render(this.valueProxy.value);
            },
            onComplete: () => {
                this.valueProxy.value = nextValue;
                this.columns.forEach((column: TColumn) => {
                    column.stepProxy.value = this.getStepValue(nextValue, column.place);
                });
                this.render(nextValue);
            }
        });

        timeline.to(this.valueProxy, {
            ...vars,
            value    : nextValue,
            overwrite: 'auto'
        }, 0);

        this.columns.forEach((column: TColumn) => {
            timeline.to(column.stepProxy, {
                ...vars,
                value    : this.getStepValue(nextValue, column.place),
                overwrite: 'auto'
            }, 0);
        });

        return timeline;
    }

    private resolveNumbersWrapper(counter: HTMLElement): HTMLElement {
        let numbersWrapper = counter.querySelector('.j-page-loader-counter-numbers') as HTMLElement | null;

        if (numbersWrapper) {
            numbersWrapper.innerHTML = '';

            return numbersWrapper;
        }

        numbersWrapper = document.createElement('div');
        numbersWrapper.className = 'page-loader__counter-numbers j-page-loader-counter-numbers';

        const postfix = counter.querySelector('.page-loader__counter-postfix');

        if (postfix) {
            counter.insertBefore(numbersWrapper, postfix);
        } else {
            counter.appendChild(numbersWrapper);
        }

        return numbersWrapper;
    }

    private buildColumns(): void {
        const places = this.getPlaces();

        this.columns = [];
        this.numbersWrapper.innerHTML = '';

        places.forEach((place: number) => {
            const viewport = document.createElement('div');
            const reel = document.createElement('div');
            const maxStep = Math.floor(this.maxValue / place);
            const revealStart = place === 1 ? 0 : place * REVEAL_RATIO;

            viewport.className = 'page-loader__counter-column';
            reel.className = 'page-loader__counter-column-track';

            if (place === 1) {
                viewport.classList.add('page-loader__counter-column--ones');
            }

            if (place === places[0] && place > 1) {
                viewport.classList.add('page-loader__counter-column--leading');
            }

            Array.from({length: maxStep + 1}, (_: unknown, index: number) => {
                return String((maxStep - index) % 10);
            }).forEach((digit: string) => {
                const digitElement = document.createElement('span');

                digitElement.className = 'page-loader__counter-digit';
                digitElement.textContent = digit;

                reel.appendChild(digitElement);
            });

            viewport.appendChild(reel);
            this.numbersWrapper.appendChild(viewport);

            this.columns.push({
                viewport,
                reel,
                place,
                maxStep,
                baseWidth: 0,
                revealStart,
                stepProxy: {value: 0}
            });
        });
    }

    private getPlaces(): number[] {
        if (this.maxValue >= 100) {
            return [100, 10, 1];
        }

        if (this.maxValue >= 10) {
            return [10, 1];
        }

        return [1];
    }

    private measure(): void {
        this.columns.forEach((column: TColumn) => {
            column.viewport.style.width = '';
            column.viewport.style.opacity = '1';
        });

        this.digitHeight = this.columns[0]?.reel.firstElementChild?.getBoundingClientRect().height || 0;

        this.columns.forEach((column: TColumn) => {
            column.baseWidth = column.viewport.getBoundingClientRect().width;
        });
    }

    private render(rawValue: number): void {
        const value = this.clamp(rawValue, 0, this.maxValue);

        this.counterWrapper.setAttribute('aria-label', `${Math.round(value)}%`);

        this.columns.forEach((column: TColumn) => {
            const visibility = column.place === 1 ?
                1 :
                this.calcVisibility(value, column.revealStart, column.place);
            const translateY = -(this.digitHeight * (column.maxStep - column.stepProxy.value));

            column.viewport.style.width = `${column.baseWidth * visibility}px`;
            column.viewport.style.opacity = `${visibility}`;
            column.reel.style.transform = `translate3d(0, ${translateY}px, 0)`;
        });
    }

    private calcVisibility(value: number, revealStart: number, maxValue: number): number {
        if (value <= revealStart) {
            return 0;
        }

        return this.clamp((value - revealStart) / (maxValue - revealStart), 0, 1);
    }

    private clamp(value: number, min: number, max: number): number {
        return Math.min(Math.max(value, min), max);
    }

    private getStepValue(value: number, place: number): number {
        return Math.floor(this.clamp(value, 0, this.maxValue) / place);
    }
}

export default Odometr;
